/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Card, Container } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import { useFormikContext } from "formik";

import Header, { HeaderType } from "../../components/Header";
import {
  Form,
  TextField,
  SelectField,
  TextArea,
  Checkbox,
  SubmitButton,
  FormHeader,
  DatePickerField,
  NumberField,
  TimePickerField,
} from "./components/FormElements";

import { getAccessToken, useAuth } from "../../context/auth";
import {
  CreateAccidentSupportService,
  GetForms,
  ViewDetail,
  ViewAccidentDetail,
} from "../../services/api/accident-service";
import type { AccidentSupportRequest } from "../../services/model/accidentSupportRequest";
import usePosition from "../../hooks/usePosition";
import { environment } from "../../environment/environment";
import {
  GetCurrentForm,
  type INavigationData,
  NavigateTo,
  SetLocation,
} from "../../helper/navigation";

import { useConfirm } from "@/components/dialog/confirm";
import BrandLoader from "@/branding/BrandLoader";
import { BrandingContext } from "@/branding/BrandingProvider";

import AddressField from "@/components/GoogleAddress/AddressField";
import { GetAddressList } from "@/services/api/address-service";

import VehicleFields from "./components/vehicle/VehicleFields";
import { useBackWithinFlow } from "./useBackWithinFlow";

import CameraCaptureCard from "./components/widgets/CameraCaptureCard";
import VehicleScanCard from "./components/widgets/VehicleScanCard";
import VoiceRecorderCard from "./components/widgets/VoiceRecorderCard";

import {
  stableStringify,
  hashString,
  stripTransientKeys,
  stripInternalHydrationKeys,

  LIST_MODE_KEY,
  LIST_DRAFT_ID_KEY,
  NEW_LIST_RECORD_ID_KEY,
  getListMode,
  getEditListId,
  ensureListDraftId,
  ensureNewListRecordId,
  getAccidentIdForDraft,
  buildDraftKey,

  setListOrigin,
  getListOrigin,
  clearListOrigin,

  hasCameraEvidence,
  hasVoiceEvidence,
  isVehicleComplete,

  clearFormCache,

  buildInitialValuesFromAnswerMap,
  buildDefaultsFromSchema,
  asImageDataUrl,
} from "./helpers/genericForm.helper";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  setDraft as setDraftAction,
  clearDraft as clearDraftAction,
  setRehydrateBaseline,
  clearBaseline,
  selectDraftByKey,
  selectDidRehydrateByKey,
  selectBaselineHashByKey,
} from "@/store/genericForm/genericFormSlice";

/* -------------------- Draft autosave (Formik) -------------------- */
function DraftAutosave({
  onSave,
  enabled = true,
}: {
  onSave: (values: Record<string, any>) => void;
  enabled?: boolean;
}) {
  const { values } = useFormikContext<Record<string, any>>();
  const lastSavedRef = React.useRef<string>("");

  useEffect(() => {
    if (!enabled) return;

    const payload = stableStringify(stripTransientKeys(values));
    if (payload === lastSavedRef.current) return;

    const t = setTimeout(() => {
      onSave(values);
      lastSavedRef.current = payload;
    }, 150);

    return () => clearTimeout(t);
  }, [enabled, values, onSave]);

  return null;
}

function FormikBridge({
  onReady,
}: {
  onReady: (api: {
    setValues: (v: any, shouldValidate?: boolean) => void;
    setFieldValue: (k: string, v: any, shouldValidate?: boolean) => void;
    getValues: () => any;
  }) => void;
}) {
  const { setValues, setFieldValue, values } = useFormikContext<any>();
  const valuesRef = React.useRef(values);

  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  useEffect(() => {
    onReady({
      setValues,
      setFieldValue,
      getValues: () => valuesRef.current,
    });
  }, [onReady, setValues, setFieldValue]);

  return null;
}

function VehicleWidget({
  elementSchema,
  goToVehicleUpload,
}: {
  elementSchema: any;
  goToVehicleUpload: () => void;
}) {
  const { values } = useFormikContext<Record<string, any>>();

  return (
    <VehicleScanCard
      label={String(elementSchema?.label ?? "Vehicle")}
      required={!!elementSchema?.required}
      licenseDiskDoc={(values as any)?.licenseDiskDoc}
      onScan={goToVehicleUpload}
      isComplete={() => isVehicleComplete(values)}
    >
      <VehicleFields />
    </VehicleScanCard>
  );
}

interface GenericFromProps {
  style?: any;
  loading: boolean;
}

const GenericForm: React.FC<GenericFromProps> = () => {
  const { authTokens } = useAuth();
  const token = getAccessToken(authTokens) ?? "";
  const confirm = useConfirm();

  const navigate = useNavigate();
  const { tenant } = useParams<{ tenant: string }>();
  const { latitude, longitude } = usePosition({
    enableHighAccuracy: true,
    watch: false,
    maximumAge: 60000,
  });

  const branding = useContext(BrandingContext);
  const enableGoogleMaps = !!branding?.enableGoogleMaps;

  const [sp] = useSearchParams();
  const step = sp.get("step") ?? "0";
  const node = sp.get("node") ?? "";
  const cameFromList = sp.get("from") === "list";
  const fromListLike = useMemo(() => {
    if (cameFromList) return true;
    const wantsListReturn = localStorage.getItem("returnToList") === "1";
    if (!wantsListReturn) return false;
    const rs = localStorage.getItem("returnStep") ?? "";
    const rn = localStorage.getItem("returnNode") ?? "";
    return rs === step && rn === node;
  }, [cameFromList, step, node]);
  const cameFromFinal =
    sp.get("from") === "final" ||
    sessionStorage.getItem("returnToFinal") === "1";

  const [formSchema, setFormSchema] = useState<Record<string, any>>({});
  const [validationSchema, setValidationSchema] = useState<any>(null);
  const [formInitialValues, setFormInitialValues] = useState<Record<string, any>>({});

  const [supportNo, SetSupportNo] = useState(0);
  const [loading, setLoading] = useState(false);
  const [allowDraftAutosave, setAllowDraftAutosave] = useState(false);
  const [, setDocCounter] = useState(0);
  const [, setVoiceCounter] = useState(0);

  const formikApiRef = useRef<null | {
    setValues: (v: any, shouldValidate?: boolean) => void;
    setFieldValue: (k: string, v: any, shouldValidate?: boolean) => void;
    getValues: () => any;
  }>(null);

  // if hydration happens before FormikBridge is ready
  const pendingFormikSetRef = useRef<Record<string, any> | null>(null);

  const pageKey = useMemo(() => `${step}-${node}`, [step, node]);
  const accidentIdForDraft = getAccidentIdForDraft();
  const editListId = getEditListId();

  // list mode
  const listMode = useMemo(
    () => (fromListLike ? sessionStorage.getItem(LIST_MODE_KEY) : null),
    [fromListLike, step, node],
  );
  const effectiveListMode = useMemo(() => {
    if (!fromListLike) return null;
    if (listMode === "new" || listMode === "edit") return listMode;
    const returnToList = localStorage.getItem("returnToList") === "1";
    if (returnToList && !editListId) return "new";
    return null;
  }, [fromListLike, listMode, editListId]);
  const isNewListItem = fromListLike && effectiveListMode === "new";

  useEffect(() => {
    if (fromListLike && effectiveListMode === "new") {
      sessionStorage.setItem(LIST_MODE_KEY, "new");
      ensureListDraftId();
    }
  }, [fromListLike, effectiveListMode]);

  // build draftKey (single source of truth)
  const draftKey = useMemo(() => {
    return buildDraftKey({
      tenant: tenant ?? "host",
      accidentId: accidentIdForDraft ?? "no-accident",
      step,
      node,
      cameFromList: fromListLike,
      listMode: effectiveListMode,
      listDraftId: sessionStorage.getItem(LIST_DRAFT_ID_KEY),
      editListId,
    });
  }, [
    tenant,
    accidentIdForDraft,
    step,
    node,
    fromListLike,
    effectiveListMode,
    editListId,
  ]);

  const dispatch = useAppDispatch();

  const reduxDraft = useAppSelector(selectDraftByKey(draftKey));
  const didRehydrate = useAppSelector(selectDidRehydrateByKey(draftKey));
  const baselineHash = useAppSelector(selectBaselineHashByKey(draftKey));
  const sanitizedDraft = useMemo(
    () => stripTransientKeys(reduxDraft ?? {}),
    [reduxDraft],
  );
  const hasLocalDraft = useMemo(
    () => {
      const hasMeaningfulValue = (v: any): boolean => {
        if (v == null) return false;
        if (typeof v === "string") return v.trim().length > 0;
        if (typeof v === "number") return Number.isFinite(v);
        if (typeof v === "boolean") return v === true;
        if (Array.isArray(v)) return v.some(hasMeaningfulValue);
        if (typeof v === "object") return Object.values(v).some(hasMeaningfulValue);
        return false;
      };
      return (
        Object.keys(sanitizedDraft).length > 0 &&
        Object.values(sanitizedDraft).some(hasMeaningfulValue)
      );
    },
    [sanitizedDraft],
  );

  // list instance key (forces remount)
  const listInstanceKey = useMemo(() => {
    if (!fromListLike) return pageKey;

    if (effectiveListMode === "new") {
      return `${pageKey}-new-${sessionStorage.getItem(LIST_DRAFT_ID_KEY) ?? "0"}`;
    }
    if (effectiveListMode === "edit") {
      return `${pageKey}-edit-${editListId ?? "0"}`;
    }
    return pageKey;
  }, [fromListLike, pageKey, effectiveListMode, editListId]);

  const saveDraft = useCallback(
    (values: Record<string, any>) => {
      dispatch(
        setDraftAction({
          draftKey,
          values: stripTransientKeys(values),
        }),
      );
    },
    [dispatch, draftKey],
  );

  const clearDraft = useCallback(() => {
    dispatch(clearDraftAction({ draftKey }));
    dispatch(clearBaseline({ draftKey }));
  }, [dispatch, draftKey]);

  useEffect(() => {
    if (fromListLike) {
      sessionStorage.removeItem("returnToFinal");
      sessionStorage.removeItem("finalReturnUrl");
    }
  }, [fromListLike]);

  // origin tracking for list navigation
  useEffect(() => {
    if (fromListLike) setListOrigin(step, node);
  }, [fromListLike, step, node]);

  const goToList = () => {
    const base = tenant ? `/${tenant}` : "";
    const origin = getListOrigin(step, node);

    const s = Number(origin.step ?? step ?? "0");
    const n = origin.node ?? node;

    SetLocation(s);
    navigate(`${base}/dynamiclist?step=${s}&node=${encodeURIComponent(n)}`, {
      replace: true,
    });

    clearListOrigin();
  };

  const goToFinal = () => {
    const url = sessionStorage.getItem("finalReturnUrl");
    if (url) {
      navigate(url, { replace: true });
      return;
    }
    const base = tenant ? `/${tenant}` : "";
    navigate(`${base}/accidentSupportFinal`, { replace: true });
  };

  const navigateAfterSubmit = () => {
    if (!fromListLike && cameFromFinal) {
      goToFinal();
      return;
    }

    const current = Number(
      new URLSearchParams(window.location.search).get("step") ??
        step ??
        "0",
    );
    SetLocation(current + 1);
    const nextUrl = NavigateTo().replace(/^\/+/, "");
    navigate(tenant ? `/${tenant}/${nextUrl}` : `/${nextUrl}`);
  };

  const showDialog = (
    title: string,
    message: string,
    success: boolean = false,
  ) => {
    confirm({ catchOnCancel: false, title, description: message }).then(() => {
      if (!success) return;

      localStorage.removeItem("linkedDocumentID");
      localStorage.removeItem("documentCount");
      localStorage.removeItem("voiceCount");

      const current = Number(step ?? "0");
      SetLocation(current + 1);
      const nextUrl = NavigateTo().replace(/^\/+/, "");
      navigate(tenant ? `/${tenant}/${nextUrl}` : `/${nextUrl}`);
    });
  };

  const goToSingleUpload = (required?: boolean, previewFieldKey?: string) => {
    const currentValues = formikApiRef.current?.getValues?.();
    if (currentValues) {
      saveDraft(currentValues);
    }

    const tempAnswerId = uuidv4();
    localStorage.setItem("linkedDocumentID", JSON.stringify(tempAnswerId));

    const base = tenant ? `/${tenant}` : "";
    const qs = window.location.search;
    const returnTo = `${base}/dynamicform${qs}`;
    const encoded = encodeURIComponent(returnTo);
    const requiredFlag = required ? "&required=1" : "";
    if (previewFieldKey) {
      localStorage.setItem("activeCameraPreviewField", previewFieldKey);
    }
    navigate(
      `${base}/singleUpload${qs}${qs ? "&" : "?"}return=${encoded}${requiredFlag}`,
    );
  };

  const goToVehicleUpload = () => {
    const currentValues = formikApiRef.current?.getValues?.();
    if (currentValues) {
      saveDraft(currentValues);
    }

    // vehicle id for the ANSWER vehicle (third party/witness vehicle)
    localStorage.removeItem("dynamicViewVehicleId");

    const base = tenant ? `/${tenant}` : "";
    const qs = window.location.search;
    const returnTo = `${base}/dynamicform${qs}`;
    const encoded = encodeURIComponent(returnTo);

    navigate(`${base}/scanner${qs}${qs ? "&" : "?"}return=${encoded}`);
  };

  const isField = (v: any) => v && typeof v === "object" && !Array.isArray(v);
  const getFieldName = (key: string, field: any) => field.id || key;
  const attachLocalCameraPreviews = useCallback(
    (schema: Record<string, any>, values: Record<string, any>) => {
      const merged = { ...(values ?? {}) };
      for (const [k, field] of Object.entries(schema)) {
        if (!isField(field)) continue;
        if (String((field as any)?.type ?? "").toLowerCase() !== "camera") continue;
        const formKey = getFieldName(k, field);
        const previewKey = `${formKey}__previewDoc`;
        if (typeof merged[previewKey] === "string" && merged[previewKey].trim()) {
          continue;
        }
        const preview = localStorage.getItem(`cameraPreview:${previewKey}`) ?? "";
        if (preview.trim()) merged[previewKey] = preview;
      }
      return merged;
    },
    [],
  );

  // voice upload helpers
  const blobToBase64 = (blob: Blob) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });

  const uploadRecording = useCallback(
    async (blobData: Blob) => {
      setLoading(true);
      try {
        const newFile = await blobToBase64(blobData);

        const recordingAnswerId = uuidv4();
        localStorage.setItem("recordingId", JSON.stringify(recordingAnswerId));

        const documents = {
          linkedId: recordingAnswerId,
          fileName: `digialert_pwa_${recordingAnswerId}`,
          file: newFile,
          type: "wav",
          documentTypeId: "00000000-0000-0000-0000-000000000004",
          formId: null,
        };

        const config = { headers: { Authorization: `Bearer ${token}` } };
        const resp = await axios.post(
          `${environment.baseUrl}/document`,
          documents,
          config,
        );

        if (resp?.status === 200) {
          const docCount = JSON.parse(
            localStorage.getItem("voiceCount") || "0",
          );
          const newCount = Number(docCount) + 1;
          localStorage.setItem("voiceCount", JSON.stringify(newCount));
          setVoiceCounter(newCount);
        }
      } catch (error) {
        if (error instanceof Error) console.error(error.message);
      } finally {
        setLoading(false);
      }
    },
    [token],
  );

  const setRecordingState = useCallback(
    async (blobUrl: string, blob: Blob) => {
      localStorage.setItem("currentRecording", JSON.stringify(blobUrl));
      await uploadRecording(blob);
    },
    [uploadRecording],
  );

  // init form = ONLY build validation
  const initForm = useCallback((fs: Record<string, any>) => {
    const _validationSchema: Record<string, any> = {};

    for (const key of Object.keys(fs)) {
      const field = fs[key];
      if (!isField(field)) continue;

      const formKey = getFieldName(key, field);

      // base validation
      switch (field.type) {
        case "text":
        case "textarea":
        case "address":
          _validationSchema[formKey] = Yup.string();
          break;
        case "email":
          _validationSchema[formKey] = Yup.string().email("Invalid email");
          break;
        case "select":
          {
            const allowed = (field.options ?? []).map(
              (o: { value: any }) => o.value,
            );
            _validationSchema[formKey] = Yup.string().oneOf(
              field.required ? allowed : [...allowed, ""],
              "Invalid option",
            );
          }
          break;
        case "checkbox":
          _validationSchema[formKey] = Yup.boolean();
          break;
        case "date":
        case "time":
          _validationSchema[formKey] = Yup.mixed().nullable();
          break;
        case "number": {
          let schema = Yup.number().typeError("Must be a number");
          if (typeof field.min === "number") schema = schema.min(field.min);
          if (typeof field.max === "number") schema = schema.max(field.max);
          _validationSchema[formKey] = schema;
          break;
        }
        default:
          break;
      }

      // required rules
      if (field.required) {
        if (field.type === "vehicle") {
          _validationSchema[formKey] = Yup.mixed().test(
            "required-vehicle",
            "Please scan the license disc before continuing.",
            () => isVehicleComplete(formikApiRef.current?.getValues?.() ?? {}),
          );
        } else if (field.type === "camera") {
          _validationSchema[formKey] = Yup.mixed().test(
            "required-camera",
            "Please capture at least one photo before continuing.",
            () => hasCameraEvidence(),
          );
        } else if (field.type === "voice") {
          _validationSchema[formKey] = Yup.mixed().test(
            "required-voice",
            "Please record audio before continuing.",
            () => hasVoiceEvidence(),
          );
        } else if (field.type === "date") {
          _validationSchema[formKey] = (_validationSchema[formKey] || Yup.mixed())
            .nullable()
            .test("required-date", "Required", (val: any) => {
              if (val == null) return false;
              if (val instanceof Date) return !isNaN(val.getTime());
              if (typeof val === "string") return val.trim().length > 0;
              return false;
            });
        } else {
          _validationSchema[formKey] = (_validationSchema[formKey] || Yup.mixed()).required("Required");
        }
      }
    }

    setValidationSchema(Yup.object().shape(_validationSchema));
  }, []);

  // Apply values to Formik safely (works even if bridge not ready yet)
  const applyFormikValues = useCallback((values: Record<string, any>) => {
    const api = formikApiRef.current;
    if (!api) {
      pendingFormikSetRef.current = values;
      return;
    }
    api.setValues(values, false);
  }, []);

  // Hydrate from server (ViewAccidentDetail => formSections.answers)
  const hydrateFromServer = useCallback(
    async (
      schema: Record<string, any>,
      nodeId: string,
      opts?: { applyFormik?: boolean; persistDraft?: boolean },
    ) => {
      const shouldApplyFormik = opts?.applyFormik ?? true;
      const shouldPersistDraft = opts?.persistDraft ?? true;
      if (isNewListItem) return null;

      const accidentId = (() => {
        try {
          const j = JSON.parse(localStorage.getItem("accidentId") || "null");
          return typeof j === "string" ? j : j == null ? null : String(j);
        } catch {
          const raw = localStorage.getItem("accidentId");
          return raw ? String(raw) : null;
        }
      })();

      if (!accidentId) return null;

      let filtered: any[] = [];

      // Deterministic hydration for list edit mode.
      if (fromListLike && effectiveListMode === "edit" && editListId) {
        const itemResp = await ViewDetail(token, accidentId, nodeId, editListId);
        const itemData =
          (itemResp as any)?.data?.data ?? (itemResp as any)?.data;
        filtered = Array.isArray(itemData)
          ? itemData
          : [];
      }

      // Fallback: hydrate from registration detail response.
      if (!filtered.length) {
        const detailResp = await ViewAccidentDetail(token, accidentId);
        const root =
          (detailResp as any)?.data?.data ?? (detailResp as any)?.data ?? detailResp;

        const sections = root?.formSections;
        if (!Array.isArray(sections) || !sections.length) return null;

        const section = sections.find(
          (s: any) => String(s?.formId ?? "") === String(nodeId),
        );
        if (!section) return null;

        const answers: any[] = Array.isArray(section?.answers)
          ? section.answers
          : [];
        if (!answers.length) return null;

        // Pick listId:
        // - edit list = use ListId
        // - otherwise pick latest listId by createdAt
        const targetListId = (() => {
          if (fromListLike && effectiveListMode === "edit") return editListId;

          const byList = new Map<string, { createdAt: number }>();
          for (const a of answers) {
            const lid = String(a?.listId ?? "");
            if (!lid) continue;
            const t = Date.parse(a?.createdAt ?? "") || 0;
            const cur = byList.get(lid);
            if (!cur || t > cur.createdAt) byList.set(lid, { createdAt: t });
          }
          let best: string | null = null;
          let bestT = -1;
          for (const [lid, meta] of byList.entries()) {
            if (meta.createdAt > bestT) {
              bestT = meta.createdAt;
              best = lid;
            }
          }
          return best;
        })();

        filtered = targetListId
          ? answers.filter((a) => String(a?.listId ?? "") === String(targetListId))
          : answers;
      }

      if (!filtered.length) return null;

      // Build answerMap keyed by questionId (schema uses id)
      const answerMap: Record<string, any> = {};
      const extraVehicleFields: Record<string, any> = {};
      const extraCameraFields: Record<string, any> = {};
      const normalizeQuestionType = (value: any): string =>
        String(value ?? "")
          .trim()
          .toLowerCase()
          .replace(/[\s_-]+/g, "");
      const toNumberOrUndefined = (n: any): number | undefined => {
        if (typeof n === "number" && Number.isFinite(n)) return n;
        if (typeof n === "string" && n.trim().length > 0) {
          const parsed = Number(n);
          return Number.isFinite(parsed) ? parsed : undefined;
        }
        return undefined;
      };
      const firstNonEmptyString = (...values: any[]): string => {
        for (const v of values) {
          if (typeof v === "string" && v.trim().length > 0) return v.trim();
        }
        return "";
      };
      const parseJsonObjectMaybe = (v: any): Record<string, any> | null => {
        if (!v) return null;
        if (typeof v === "object" && !Array.isArray(v)) return v as Record<string, any>;
        if (typeof v !== "string") return null;
        const trimmed = v.trim();
        if (!(trimmed.startsWith("{") && trimmed.endsWith("}"))) return null;
        try {
          const parsed = JSON.parse(trimmed);
          return parsed && typeof parsed === "object" && !Array.isArray(parsed)
            ? (parsed as Record<string, any>)
            : null;
        } catch {
          return null;
        }
      };
      const isAddressLikeAnswer = (answerRow: any): boolean => {
        if (answerRow?.address && typeof answerRow.address === "object") return true;

        const parsed = parseJsonObjectMaybe(answerRow?.answer);
        if (!parsed) return false;

        const nested =
          parsed?.address && typeof parsed.address === "object" ? parsed.address : null;

        const hasAddressText =
          typeof parsed.googleAddress === "string" ||
          typeof parsed.address === "string" ||
          typeof parsed.formattedAddress === "string" ||
          typeof nested?.googleAddress === "string" ||
          typeof nested?.address === "string" ||
          typeof nested?.formattedAddress === "string";

        const hasCoords =
          parsed.latitude != null ||
          parsed.lat != null ||
          parsed.longitude != null ||
          parsed.lng != null ||
          nested?.latitude != null ||
          nested?.lat != null ||
          nested?.longitude != null ||
          nested?.lng != null;

        return hasAddressText || hasCoords;
      };
      const normalizeLabel = (value: any): string =>
        String(value ?? "")
          .toLowerCase()
          .replace(/\*/g, "")
          .replace(/[:]/g, "")
          .replace(/\s+/g, " ")
          .trim();

      const schemaFields = Object.entries(schema)
        .filter(([, f]) => isField(f))
        .map(([k, f]) => ({
          key: k,
          id: String((f as any)?.id ?? k).trim(),
          labelNorm: normalizeLabel((f as any)?.label ?? k),
          typeNorm: String((f as any)?.type ?? "")
            .trim()
            .toLowerCase(),
        }));

      const schemaKeyById = new Map<string, string>();
      for (const sf of schemaFields) {
        schemaKeyById.set(sf.id, sf.id);
        schemaKeyById.set(sf.id.toLowerCase(), sf.id);
        schemaKeyById.set(sf.key, sf.id);
        schemaKeyById.set(sf.key.toLowerCase(), sf.id);
      }

      const normalizeAddressAnswer = (answerRow: any) => {
        const raw = answerRow?.answer;
        let parsed: any = raw;

        if (typeof raw === "string") {
          const trimmed = raw.trim();
          if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
            try {
              parsed = JSON.parse(trimmed);
            } catch {
              parsed = raw;
            }
          }
        }

        const parsedObj =
          parsed && typeof parsed === "object" && !Array.isArray(parsed)
            ? parsed
            : null;
        const nestedAddress =
          parsedObj?.address &&
          typeof parsedObj.address === "object" &&
          !Array.isArray(parsedObj.address)
            ? parsedObj.address
            : answerRow?.address &&
                typeof answerRow.address === "object" &&
                !Array.isArray(answerRow.address)
              ? answerRow.address
              : null;

        const latitude =
          toNumberOrUndefined(
            parsedObj?.latitude ??
              parsedObj?.lat ??
              nestedAddress?.latitude ??
              nestedAddress?.lat ??
              answerRow?.latitude ??
              answerRow?.lat,
          ) ??
          undefined;
        const longitude =
          toNumberOrUndefined(
            parsedObj?.longitude ??
              parsedObj?.lng ??
              nestedAddress?.longitude ??
              nestedAddress?.lng ??
              answerRow?.longitude ??
              answerRow?.lng,
          ) ??
          undefined;

        const googleAddress = firstNonEmptyString(
          parsedObj?.googleAddress,
          typeof parsedObj?.address === "string" ? parsedObj.address : "",
          parsedObj?.formattedAddress,
          nestedAddress?.googleAddress,
          nestedAddress?.address,
          nestedAddress?.formattedAddress,
          answerRow?.googleAddress,
          answerRow?.formattedAddress,
          typeof raw === "string" ? raw : "",
        );

        return { googleAddress, latitude, longitude };
      };

      for (const a of filtered) {
        const qid = String(a?.questionId ?? "").trim();
        const qTextNorm = normalizeLabel(a?.question);

        let targetKey = "";
        if (qid) {
          targetKey =
            schemaKeyById.get(qid) ??
            schemaKeyById.get(qid.toLowerCase()) ??
            "";
        }
        if (!targetKey && qTextNorm) {
          const match = schemaFields.find(
            (sf) =>
              sf.labelNorm === qTextNorm ||
              sf.labelNorm.includes(qTextNorm) ||
              qTextNorm.includes(sf.labelNorm),
          );
          if (match) {
            targetKey = match.id;
          }
        }
        if (!targetKey) {
          const qtFallback = normalizeQuestionType(a?.questionType);
          if (qtFallback.includes("address") || isAddressLikeAnswer(a)) {
            const addressField = schemaFields.find(
              (sf) => sf.typeNorm === "address" && !(sf.id in answerMap),
            );
            if (addressField) {
              targetKey = addressField.id;
            }
          }
        }
        if (!targetKey) continue;

        const qt = normalizeQuestionType(a?.questionType);
        const q = String(a?.question ?? "").toLowerCase().trim();
        const isAddressQuestion =
          qt.includes("address") ||
          q === "address" ||
          isAddressLikeAnswer(a);

        if (isAddressQuestion) {
          answerMap[targetKey] = normalizeAddressAnswer(a);
        } else {
          answerMap[targetKey] = a?.answer ?? "";
        }

        if (qt === "camera" || q === "camera") {
          const img0 = Array.isArray(a?.images) && a.images.length ? a.images[0] : null;
          const linkedId = String(a?.answer ?? img0?.fromId ?? "").trim();
          if (linkedId) {
            localStorage.setItem("linkedDocumentID", JSON.stringify(linkedId));
          }
          if (img0?.document) {
            extraCameraFields[`${targetKey}__previewDoc`] = asImageDataUrl(
              img0.document,
            );
          }
        }

        // if this is the Vehicle question answer, also hydrate vehicle widget fields
        if (qt === "vehicle" || q === "vehicle") {
          const veh = a?.vehicle ?? {};
          const img0 = Array.isArray(a?.images) && a.images.length ? a.images[0] : null;

          // vehicleId for the ANSWER vehicle (third party/witness vehicle)
          const vehicleId = String(a?.answer ?? veh?.id ?? "").trim();
          if (vehicleId) {
            extraVehicleFields.vehicleId = vehicleId;
            localStorage.setItem("dynamicViewVehicleId", JSON.stringify(vehicleId));
          }

          extraVehicleFields.licensePlateNumber = veh?.licensePlateNumber ?? "";
          extraVehicleFields.make = veh?.make ?? "";
          extraVehicleFields.model = veh?.model ?? "";
          extraVehicleFields.color = veh?.color ?? "";
          extraVehicleFields.vin = veh?.vin ?? "";
          extraVehicleFields.engineNumber = veh?.engineNumber ?? "";

          // license disk image comes from answer.images[*].document base64
          if (img0?.document) {
            extraVehicleFields.licenseDiskDoc = asImageDataUrl(img0.document);
          } else {
            extraVehicleFields.licenseDiskDoc = "";
          }
        }
      }

      const built = buildInitialValuesFromAnswerMap(schema, answerMap);
      const addressFieldIds = schemaFields
        .filter((sf) => sf.typeNorm === "address")
        .map((sf) => sf.id);

      if (addressFieldIds.length > 0) {
        const addressCandidates = filtered
          .filter(
            (a) =>
              normalizeQuestionType(a?.questionType).includes("address") ||
              isAddressLikeAnswer(a),
          )
          .map((a) => normalizeAddressAnswer(a))
          .filter((a) => {
            const hasText =
              typeof a?.googleAddress === "string" &&
              a.googleAddress.trim().length > 0;
            const hasPoint = a?.latitude != null && a?.longitude != null;
            return hasText || hasPoint;
          });

        let candidateIdx = 0;
        for (const name of addressFieldIds) {
          const hasText =
            typeof built[name] === "string" && built[name].trim().length > 0;
          const latKey = `${name}__latitude`;
          const lngKey = `${name}__longitude`;
          const hasPoint =
            built[latKey] != null &&
            built[lngKey] != null &&
            String(built[latKey]).trim().length > 0 &&
            String(built[lngKey]).trim().length > 0;

          if (hasText || hasPoint) continue;

          const candidate =
            addressCandidates[candidateIdx] ?? addressCandidates[0] ?? null;
          if (!candidate) continue;

          if (
            typeof candidate.googleAddress === "string" &&
            candidate.googleAddress.trim().length > 0
          ) {
            built[name] = candidate.googleAddress;
          }
          if (candidate.latitude != null) built[latKey] = candidate.latitude;
          if (candidate.longitude != null) built[lngKey] = candidate.longitude;
          candidateIdx += 1;
        }
      }

      const merged = {
        ...buildDefaultsFromSchema(schema),
        ...built,
        ...extraCameraFields,
        ...extraVehicleFields,
      };

      // save draft + baseline
      if (shouldPersistDraft) {
        dispatch(
          setDraftAction({
            draftKey,
            values: stripTransientKeys(merged),
          }),
        );

        dispatch(
          setRehydrateBaseline({
            draftKey,
            didRehydrate: true,
            baselineHash: hashString(stableStringify(stripTransientKeys(merged))),
          }),
        );
      }

      // apply to formik now (or when bridge becomes ready)
      if (shouldApplyFormik) {
        applyFormikValues(merged);
      }
      return merged;
    },
    [
      token,
      isNewListItem,
      fromListLike,
      effectiveListMode,
      editListId,
      dispatch,
      draftKey,
      applyFormikValues,
    ],
  );

  // element renderer
  const getFormElement = (elementName: string, elementSchema?: any) => {
    if (!isField(elementSchema)) return null;

    const formKey = getFieldName(elementName, elementSchema);

    const props = {
      id: elementSchema.id,
      name: formKey,
      label: elementSchema.label,
      description: elementSchema.description,
      options: elementSchema.options,
      required: !!elementSchema.required,
      placeholder: elementSchema.placeholder,
    };

    if (elementName === "Form") {
      if (elementSchema.id) localStorage.setItem("formTypeId", JSON.stringify(elementSchema.id));
      return <FormHeader claimNumber={"00000" + supportNo.toString()} {...props} />;
    }

    switch (elementSchema.type) {
      case "text":
      case "email":
        return <TextField {...props} />;
      case "textarea":
        return <TextArea {...props} />;
      case "checkbox":
        return <Checkbox {...props} />;
      case "select":
        return <SelectField {...props} />;
      case "date":
        return <DatePickerField {...props} />;
      case "number":
        return <NumberField {...props} />;
      case "time":
        return <TimePickerField {...props} />;

      case "camera":
        return (
          <CameraCaptureCard
            label={String(elementSchema.label ?? "Camera")}
            required={!!elementSchema.required}
            hasEvidence={hasCameraEvidence}
            onSaveDraft={saveDraft}
            onNavigate={() =>
              goToSingleUpload(
                !!elementSchema.required,
                `${formKey}__previewDoc`,
              )
            }
            previewField={`${formKey}__previewDoc`}
          />
        );

      case "vehicle":
        return (
          <VehicleWidget
            elementSchema={elementSchema}
            goToVehicleUpload={goToVehicleUpload}
          />
        );

      case "voice":
        return (
          <VoiceRecorderCard
            label={String(elementSchema.label ?? "Voice Recorder")}
            required={!!elementSchema.required}
            hasEvidence={hasVoiceEvidence}
            getCachedRecording={() => {
              try {
                const raw = window.localStorage.getItem("currentRecording");
                return raw ? JSON.parse(raw) : null;
              } catch {
                return null;
              }
            }}
            onStop={(blobUrl: string, blob: Blob) =>
              setRecordingState(blobUrl, blob)
            }
          />
        );

      case "address":
        return (
          <AddressField
            key={`${elementSchema.id}-${draftKey}`}
            id={elementSchema.id}
            name={formKey}
            label={elementSchema.label}
            required={!!elementSchema.required}
            enableMap={true}
            enableGoogleMaps={enableGoogleMaps}
            token={token}
            GetAddressList={GetAddressList}
            showDialog={showDialog}
          />
        );

      default:
        return null;
    }
  };

  // cancel/back
  const cancel = () => {
    clearFormCache();
    clearDraft();

    if (fromListLike) {
      goToList();
      return;
    }

    const current = Number(step || "0");
    if (current <= 0) {
      const incidentType = JSON.parse(
        localStorage.getItem("incidentType") || "null",
      );
      if (incidentType === "AccidentSupport") {
        navigate(tenant ? `/${tenant}/accidentSupport` : `/accidentSupport`, {
          replace: true,
        });
      } else {
        navigate(
          tenant ? `/${tenant}/accidentSupportList` : `/accidentSupportList`,
          { replace: true },
        );
      }
      return;
    }

    SetLocation(current - 1);
    const prevUrl = NavigateTo().replace(/^\/+/, "");
    navigate(tenant ? `/${tenant}/${prevUrl}` : `/${prevUrl}`);
  };

  // submit (kept as you had it, unchanged logic)
  const onSubmit = async (
    values: any,
    formikHelpers: { setSubmitting: (v: boolean) => void },
  ) => {
    const { setSubmitting } = formikHelpers;

    try {
      const hasRequiredVoiceQuestion = Object.values(formSchema).some(
        (field: any) =>
          field &&
          typeof field === "object" &&
          !Array.isArray(field) &&
          String(field.type ?? "").toLowerCase() === "voice" &&
          !!field.required,
      );

      if (hasRequiredVoiceQuestion && !hasVoiceEvidence()) {
        showDialog(
          "Required",
          "Please record audio before continuing.",
          false,
        );
        return;
      }

      const submissionValues: Record<string, any> = stripInternalHydrationKeys({
        ...(values ?? {}),
      });

      // unchanged short-circuit (only for rehydrated edit/back)
      if (!isNewListItem && didRehydrate && baselineHash) {
        const currentHash = hashString(
          stableStringify(stripTransientKeys(values)),
        );
        if (currentHash === baselineHash) {
          const wantList = localStorage.getItem("returnToList") === "1";
          const rStep = localStorage.getItem("returnStep");
          const rNode = localStorage.getItem("returnNode");
          const base = tenant ? `/${tenant}` : "";

          const s = Number(step ?? "0");
          localStorage.setItem(`flowStepDone:${s}:${node}`, "1");

          if (wantList && rStep && rNode) {
            SetLocation(Number(rStep));
            navigate(
              `${base}/dynamiclist?step=${rStep}&node=${encodeURIComponent(
                rNode,
              )}`,
              { replace: true },
            );

            localStorage.removeItem("returnToList");
            localStorage.removeItem("returnStep");
            localStorage.removeItem("returnNode");
            clearFormCache();
            clearDraft();
            sessionStorage.removeItem(LIST_MODE_KEY);
            sessionStorage.removeItem(LIST_DRAFT_ID_KEY);
            sessionStorage.removeItem(NEW_LIST_RECORD_ID_KEY);
            return;
          }

          const current = Number(
            new URLSearchParams(window.location.search).get("step") ??
              step ??
              "0",
          );
          if (!fromListLike && cameFromFinal) {
            goToFinal();
          } else {
            SetLocation(current + 1);
            const nextUrl = NavigateTo().replace(/^\/+/, "");
            navigate(tenant ? `/${tenant}/${nextUrl}` : `/${nextUrl}`);
          }
          clearDraft();
          return;
        }
      }

      const currentLatitude = String(latitude ?? "");
      const currentLongitude = String(longitude ?? "");

      // accident id
      const existing = window.localStorage.getItem("accidentId");
      let accidentId: any;
      if (!existing) {
        accidentId = JSON.stringify(uuidv4());
        localStorage.setItem("accidentId", accidentId);
      } else {
        accidentId = JSON.parse(existing || "");
      }

      // vehicleId (global main claim vehicle) - leave as you had it
      let vehicleId: string = "00000000-0000-0000-0000-000000000000";
      const incidentType = JSON.parse(
        window.localStorage.getItem("incidentType") || "null",
      );
      if (incidentType === "AccidentSupport") {
        vehicleId = JSON.parse(
          window.localStorage.getItem("VehicleId") ||
            "00000000-0000-0000-0000-000000000000",
        );
      }

      const formId = JSON.parse(
        window.localStorage.getItem("formId") || "null",
      );

      const schemaFields = Object.entries(formSchema).filter(([, f]) =>
        isField(f),
      ) as Array<[string, any]>;

      const mediaFieldByType = (type: string) =>
        schemaFields.find(
          ([, f]) => String(f?.type ?? "").toLowerCase() === type,
        );

      const readLsId = (key: string): string => {
        const raw = window.localStorage.getItem(key);
        if (!raw) return "";
        try {
          const parsed = JSON.parse(raw);
          return String(parsed ?? "").trim();
        } catch {
          return raw.trim();
        }
      };

      // Backend addForm expects Camera/Voice/Vehicle special keys.
      // Remove media question-id fields from payload to avoid empty value collisions.
      for (const [schemaKey, field] of schemaFields) {
        const t = String(field?.type ?? "").toLowerCase();
        if (t === "camera" || t === "voice" || t === "vehicle") {
          const formKey = getFieldName(schemaKey, field);
          delete submissionValues[formKey];
        }
      }

      const cameraField = mediaFieldByType("camera");
      if (cameraField) {
        const [, field] = cameraField;
        const cameraQuestionId = String(field?.id ?? "").trim();
        const questionAnswerID = readLsId("linkedDocumentID");
        submissionValues.Camera = {
          cameraQuestionId,
          questionAnswerID,
        };
      }

      const idFromFormik =
        typeof submissionValues?.vehicleId === "string"
          ? submissionValues.vehicleId.trim()
          : "";
      const idFromLs = readLsId("dynamicViewVehicleId");
      const resolvedVehicleId = idFromFormik || idFromLs;
      if (resolvedVehicleId) {
        localStorage.setItem(
          "dynamicViewVehicleId",
          JSON.stringify(resolvedVehicleId),
        );
      }

      const vehicleField = mediaFieldByType("vehicle");
      if (vehicleField) {
        const [, field] = vehicleField;
        const cameraQuestionId = String(field?.id ?? "").trim();
        submissionValues.Vehicle = {
          cameraQuestionId,
          questionAnswerID: resolvedVehicleId || "No Data",
        };
      }

      const voiceField = mediaFieldByType("voice");
      if (voiceField) {
        const [, field] = voiceField;
        const cameraQuestionId = String(field?.id ?? "").trim();
        const questionAnswerID = readLsId("recordingId");
        submissionValues.Voice = {
          cameraQuestionId,
          questionAnswerID,
        };
      }
      localStorage.removeItem("currentRecording");
      localStorage.removeItem("recordingId");

      // list mode routing id
      let listIdToSend: string | null = null;
      if (fromListLike) {
        const mode = effectiveListMode ?? getListMode();
        if (mode === "new") listIdToSend = ensureNewListRecordId();
        else if (mode === "edit") listIdToSend = getEditListId();
      }
      if (listIdToSend) submissionValues.listId = listIdToSend;

      const accident: AccidentSupportRequest = {
        id: accidentId,
        latitude: currentLatitude,
        longitude: currentLongitude,
        notes: "",
        notes2: "",
        supportNo: supportNo.toString(),
        clientId: branding?.clientId,
        questionAnswers: submissionValues,
        vehicleId,
        formId,
        status: "Pending",
      };

      const currentForm: INavigationData | null = GetCurrentForm();
      const response = await CreateAccidentSupportService(
        accident,
        currentForm?.id || "",
        token,
      );

      if (response && (response as any).data) {
        localStorage.setItem("accidentId", JSON.stringify((response as any).data.id));

        const wantList = localStorage.getItem("returnToList") === "1";
        const rStep = localStorage.getItem("returnStep");
        const rNode = localStorage.getItem("returnNode");
        const base = tenant ? `/${tenant}` : "";

        const s = Number(step ?? "0");
        localStorage.setItem(`flowStepDone:${s}:${node}`, "1");

        if (wantList && rStep && rNode) {
          SetLocation(Number(rStep));
          navigate(
            `${base}/dynamiclist?step=${rStep}&node=${encodeURIComponent(rNode)}`,
            { replace: true },
          );

          localStorage.removeItem("returnToList");
          localStorage.removeItem("returnStep");
          localStorage.removeItem("returnNode");
          clearFormCache();
          clearDraft();

          sessionStorage.removeItem(LIST_MODE_KEY);
          sessionStorage.removeItem(LIST_DRAFT_ID_KEY);
          sessionStorage.removeItem(NEW_LIST_RECORD_ID_KEY);
          return;
        }

        clearDraft();
        navigateAfterSubmit();
        return;
      }

      showDialog(
        "Failure",
        !response
          ? "There seems to be a problem with your internet connection. Please check your connection and try again."
          : "There seems to be a problem, please contact your administrator or try again.",
        false,
      );
    } catch (err) {
      setLoading(false);
      showDialog(
        "Failure",
        "An exception occurred, please contact your administrator.",
        false,
      );
      if (err instanceof Error) console.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // FetchForms (+ hydrate)
  const cancelRef = useRef(false);
  useEffect(() => {
    cancelRef.current = false;
    return () => {
      cancelRef.current = true;
    };
  }, [step, node]);

  const FetchForms = useCallback(async () => {
    setLoading(true);
    try {
      const nodeId = (node && node.trim()) || (GetCurrentForm()?.id?.trim() ?? "");
      if (!nodeId) {
        showDialog("Failure", "No nodeId found for this step/node.", false);
        return;
      }

      const resp = await GetForms(token, nodeId);
      if (cancelRef.current) return;

      // GetForms returns schema string in resp.data
      const raw = (resp as any)?.data;
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;

      if (!parsed || typeof parsed !== "object") {
        showDialog("Failure", "Invalid form schema returned by server.", false);
        return;
      }

      setFormSchema(parsed);
      initForm(parsed);

      // Keep in-progress local draft authoritative, except when returning from
      // final/view flow where server data must win.
      if (hasLocalDraft && !cameFromFinal) {
        let merged = attachLocalCameraPreviews(parsed, {
          ...buildDefaultsFromSchema(parsed),
          ...sanitizedDraft,
        });

        const addressFieldKeys = Object.entries(parsed)
          .filter(
            ([, f]) =>
              isField(f) && String((f as any)?.type ?? "").toLowerCase() === "address",
          )
          .map(([k, f]) => String((f as any)?.id ?? k));

        const hasNonEmptyString = (v: any) =>
          typeof v === "string" && v.trim().length > 0;

        const needsAddressBackfill = addressFieldKeys.some((name) => {
          const hasMainKey =
            Object.prototype.hasOwnProperty.call(sanitizedDraft, name);
          const hasLatKey = Object.prototype.hasOwnProperty.call(
            sanitizedDraft,
            `${name}__latitude`,
          );
          const hasLngKey = Object.prototype.hasOwnProperty.call(
            sanitizedDraft,
            `${name}__longitude`,
          );

          if (!hasMainKey && !hasLatKey && !hasLngKey) return true;

          const txt = merged[name];
          const lat = merged[`${name}__latitude`];
          const lng = merged[`${name}__longitude`];
          const hasPoint =
            lat != null &&
            lng != null &&
            String(lat).trim().length > 0 &&
            String(lng).trim().length > 0;
          return !hasNonEmptyString(txt) && !hasPoint;
        });

        if (needsAddressBackfill) {
          const fromServer = await hydrateFromServer(parsed, nodeId, {
            applyFormik: false,
            persistDraft: false,
          });

          if (fromServer) {
            const backfilled = { ...merged };

            for (const name of addressFieldKeys) {
              const shouldBackfillMain =
                !Object.prototype.hasOwnProperty.call(sanitizedDraft, name) ||
                !hasNonEmptyString(backfilled[name]);
              if (shouldBackfillMain) {
                const v = fromServer[name];
                if (typeof v === "string" && v.trim().length > 0) {
                  backfilled[name] = v;
                }
              }

              const latKey = `${name}__latitude`;
              const shouldBackfillLat =
                !Object.prototype.hasOwnProperty.call(sanitizedDraft, latKey) ||
                String(backfilled[latKey] ?? "").trim().length === 0;
              if (
                shouldBackfillLat &&
                fromServer[latKey] != null &&
                String(fromServer[latKey]).trim().length > 0
              ) {
                backfilled[latKey] = fromServer[latKey];
              }

              const lngKey = `${name}__longitude`;
              const shouldBackfillLng =
                !Object.prototype.hasOwnProperty.call(sanitizedDraft, lngKey) ||
                String(backfilled[lngKey] ?? "").trim().length === 0;
              if (
                shouldBackfillLng &&
                fromServer[lngKey] != null &&
                String(fromServer[lngKey]).trim().length > 0
              ) {
                backfilled[lngKey] = fromServer[lngKey];
              }
            }

            merged = attachLocalCameraPreviews(parsed, backfilled);
          }
        }

        setFormInitialValues(merged);
        applyFormikValues(merged);
        setAllowDraftAutosave(true);
        return;
      }

      if (isNewListItem) {
        ensureNewListRecordId();
        clearFormCache({ preserveEvidence: true });
        const defaults = attachLocalCameraPreviews(
          parsed,
          buildDefaultsFromSchema(parsed),
        );
        setFormInitialValues(defaults);
        applyFormikValues(defaults);
        setAllowDraftAutosave(true);
        return;
      }

      // reset baseline only when we are about to rehydrate from server
      dispatch(clearBaseline({ draftKey }));

      // ✅ hydrate from server for existing accidentId (edit/back/view)
      const hydrated = await hydrateFromServer(parsed, nodeId);
      const fallback = attachLocalCameraPreviews(
        parsed,
        hydrated ?? buildDefaultsFromSchema(parsed),
      );
      setFormInitialValues(fallback);
      if (!hydrated) applyFormikValues(fallback);
      setAllowDraftAutosave(true);
    } catch (e: any) {
      console.error("FetchForms failed:", e);
      showDialog(
        "Failure",
        "Unable to load forms. Please check your connection.",
        false,
      );
    } finally {
      if (!cancelRef.current) setLoading(false);
    }
  }, [
    token,
    node,
    initForm,
    dispatch,
    draftKey,
    isNewListItem,
    hasLocalDraft,
    cameFromFinal,
    sanitizedDraft,
    applyFormikValues,
    hydrateFromServer,
    attachLocalCameraPreviews,
  ]);

  const getRandomNumberBetween = (min: number, max: number) =>
    Math.floor(Math.random() * (max - min + 1) + min);

  useEffect(() => {
    const s = Number(step ?? "0");
    SetLocation(s);

    setFormSchema({});
    setValidationSchema(null);
    setFormInitialValues({});
    setAllowDraftAutosave(false);

    if (token) void FetchForms();

    SetSupportNo(getRandomNumberBetween(1, 9999));
    setDocCounter(JSON.parse(localStorage.getItem("documentCount") || "0"));
    setVoiceCounter(JSON.parse(localStorage.getItem("voiceCount") || "0"));
  }, [step, node, token]);

  useBackWithinFlow(() => {
    navigate(tenant ? `/${tenant}/accidentSupport` : `/accidentSupport`, {
      replace: true,
    });
  }, tenant);

  // when bridge becomes ready, apply pending setValues
  const onFormikReady = useCallback((api: any) => {
    formikApiRef.current = api;
    if (pendingFormikSetRef.current) {
      api.setValues(pendingFormikSetRef.current, false);
      pendingFormikSetRef.current = null;
    }
  }, []);

  return (
    <div key={listInstanceKey}>
      <Header type={HeaderType.AccidentSupportAdd} />
      <Container maxWidth="sm">
        <Grid container spacing={1}>
          <Grid size={12 as any}>
            <Card variant="outlined" style={{ padding: "15px" }}>
              <Grid
                container
                direction="row"
                justifyContent="center"
                spacing={1}
              >
                <Grid size={12 as any}>
                  <Form
                    key={draftKey}
                    noValidate
                    enableReinitialize
                    initialValues={formInitialValues}
                    validationSchema={validationSchema ?? undefined}
                    onSubmit={onSubmit}
                  >
                    <FormikBridge onReady={onFormikReady} />
                    <DraftAutosave
                      onSave={saveDraft}
                      enabled={allowDraftAutosave}
                    />

                    <Card variant="outlined" style={{ padding: "15px" }}>
                      {Object.entries(formSchema).map(([key, schema]) =>
                        isField(schema) ? (
                          <div key={key}>
                            <br />
                            {getFormElement(key, schema as any)}
                            <br />
                          </div>
                        ) : null,
                      )}

                      <Grid size={12 as any}>
                        <br />
                        <br />
                        {cameFromFinal && !fromListLike ? (
                          <SubmitButton title="Done" color="primary" />
                        ) : (
                          <SubmitButton title="Submit" color="primary" />
                        )}
                      </Grid>

                      <Grid size={12 as any}>
                        <br />
                        {fromListLike || !cameFromFinal ? (
                          <Button
                            type="button"
                            onClick={cancel}
                            value="cancel"
                            variant="outlined"
                            color="secondary"
                            fullWidth
                            size="large"
                          >
                            <strong>Back</strong>
                          </Button>
                        ) : null}
                      </Grid>
                    </Card>
                  </Form>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <BrandLoader open={loading} />
    </div>
  );
};

export default GenericForm;

